/**
 * <ta-edit-pitcher> — inline Edit form for the Profile tab.
 *
 * Rendered on /athletes/:id/profile next to the read-only display. Click
 * "Edit" → swaps to a form pre-filled with the athlete's current values.
 * Submit → calls updateAthlete() → emits 'athlete-updated' so the parent
 * re-renders the profile tab with fresh data.
 *
 * Set data via .athlete:
 *   const el = document.querySelector('ta-edit-pitcher');
 *   el.athlete = athleteRow;
 */

import { updateAthlete } from '../../db/queries.js';

class TaEditPitcher extends HTMLElement {
  constructor() {
    super();
    this._athlete = null;
    this._editing = false;
    this._submitting = false;
    this._error = null;
  }

  set athlete(value) {
    this._athlete = value;
    this.render();
  }
  get athlete() { return this._athlete; }

  connectedCallback() {
    this.render();
    this._onClick = (e) => this.handleClick(e);
    this._onSubmit = (e) => this.handleSubmit(e);
    this.addEventListener('click', this._onClick);
    this.addEventListener('submit', this._onSubmit);
  }

  handleClick(e) {
    if (e.target.closest('.ta-edit-pitcher__open')) {
      e.preventDefault();
      this._editing = true;
      this._error = null;
      this.render();
      this.querySelector('input[name="first_name"]')?.focus();
    } else if (e.target.closest('.ta-edit-pitcher__cancel')) {
      e.preventDefault();
      this._editing = false;
      this._error = null;
      this.render();
    }
  }

  async handleSubmit(e) {
    if (!e.target.matches('.ta-edit-pitcher__form')) return;
    e.preventDefault();
    if (this._submitting || !this._athlete) return;

    const fd = new FormData(e.target);
    const fields = {
      first_name: fd.get('first_name'),
      last_name: fd.get('last_name'),
      athlete_age: fd.get('athlete_age'),
      playing_age: fd.get('playing_age'),
      throws_hand: fd.get('throws_hand') || 'R',
      nickname: fd.get('nickname'),
    };

    this._submitting = true;
    this._error = null;
    this.render();

    try {
      const updated = await updateAthlete(this._athlete.id, fields);
      this._athlete = { ...this._athlete, ...updated };
      this._editing = false;
      this._submitting = false;
      this.render();
      this.dispatchEvent(
        new CustomEvent('athlete-updated', { bubbles: true, detail: updated })
      );
    } catch (err) {
      this._submitting = false;
      const msg = /duplicate key|23505/i.test(err.message ?? '')
        ? `An athlete with that name already exists.`
        : (err.message ?? String(err));
      this._error = msg;
      this.render();
    }
  }

  render() {
    if (!this._athlete) {
      this.innerHTML = '';
      return;
    }
    if (this._editing) {
      this.innerHTML = this.renderForm();
    } else {
      this.innerHTML = `<button type="button" class="ta-edit-pitcher__open">Edit</button>`;
    }
  }

  renderForm() {
    const a = this._athlete;
    return `
      <form class="ta-edit-pitcher__form">
        <h3 class="ta-edit-pitcher__title">Edit profile</h3>

        ${this._error ? `<div class="ta-add-pitcher__error">${escapeHtml(this._error)}</div>` : ''}

        <div class="form-row">
          <label class="form-field">
            <span class="form-field__label">First name</span>
            <input type="text" name="first_name" value="${escapeHtml(a.first_name)}" required autocomplete="off">
          </label>
          <label class="form-field">
            <span class="form-field__label">Last name</span>
            <input type="text" name="last_name" value="${escapeHtml(a.last_name)}" required autocomplete="off">
          </label>
        </div>

        <div class="form-row">
          <label class="form-field">
            <span class="form-field__label">Athlete age <span class="form-field__optional">(years)</span></span>
            <input type="number" name="athlete_age" min="5" max="60"
                   value="${a.athlete_age ?? ''}" placeholder="e.g. 13">
          </label>
          <label class="form-field">
            <span class="form-field__label">Playing age <span class="form-field__optional">(class)</span></span>
            <input type="text" name="playing_age"
                   value="${escapeHtml(a.playing_age ?? '')}" placeholder="e.g. 13U, 14U, HS">
          </label>
        </div>

        <div class="form-row">
          <fieldset class="form-field throws-toggle">
            <legend class="form-field__label">Throws</legend>
            <label><input type="radio" name="throws_hand" value="R" ${a.throws_hand === 'R' ? 'checked' : ''}> R</label>
            <label><input type="radio" name="throws_hand" value="L" ${a.throws_hand === 'L' ? 'checked' : ''}> L</label>
            <label><input type="radio" name="throws_hand" value="S" ${a.throws_hand === 'S' ? 'checked' : ''}> S</label>
          </fieldset>
          <label class="form-field">
            <span class="form-field__label">Nickname <span class="form-field__optional">(optional)</span></span>
            <input type="text" name="nickname" value="${escapeHtml(a.nickname ?? '')}" autocomplete="off">
          </label>
        </div>

        <div class="form-actions">
          <button type="button" class="ta-add-pitcher__cancel ta-edit-pitcher__cancel" ${this._submitting ? 'disabled' : ''}>Cancel</button>
          <button type="submit" class="ta-add-pitcher__submit" ${this._submitting ? 'disabled' : ''}>
            ${this._submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    `;
  }
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

customElements.define('ta-edit-pitcher', TaEditPitcher);
